import { DateFormat } from '@startuptools/ui/DateFormat';
import { Button } from '@startuptools/ui/Button';
import { Trans, useLingui } from '@lingui/react';
import { type GqlBolagoFile, type GqlMeeting, GqlMeetingKind } from '../../../../graphql/base-types.graphql';
import { MeetingProgress } from './MeetingProgress';
import { DateTime } from 'luxon';
import { DashboardTable, EmptyState } from './Table';
import { useNavigate } from '../../../../components/Navigate';
import { styled } from '@mui/joy/styles';
import { Link } from 'react-router-dom';
import { meetingLinkShowProtocol } from '../../../../components/Links';

const MeetingName = ({ kind }: { kind: GqlMeetingKind }) => {
  switch (kind) {
    case GqlMeetingKind.ShareholdersMeeting:
      return <Trans id="Annual general meeting" />;
    case GqlMeetingKind.ExtraShareholdersMeeting:
      return <Trans id="Extraordinary general meeting" />;
    case GqlMeetingKind.BoardMeeting:
      return <Trans id="Board meeting" />;
  }
};

const Tr = styled(Link)({ display: 'table-row' });

export const MeetingTable = ({
  meetings,
}: {
  meetings: (Pick<
    GqlMeeting,
    'id' | 'companyId' | 'kind' | 'number' | 'startTime' | 'summonStatus' | 'agendaItemsDone'
  > & {
    latestMinutesDocument?: Pick<GqlBolagoFile, 'scriveStatus' | 'zignedAgreementStatus'> | undefined | null;
  })[];
}) => {
  const { link } = useNavigate();
  const { i18n } = useLingui();

  const hasMeetings = meetings.length > 0;
  return (
    <DashboardTable
      title={<Trans id="Meetings" />}
      hasItems={hasMeetings}
      rows={meetings.map(meeting => (
        <Tr key={meeting.id} to={link(meetingLinkShowProtocol(meeting))}>
          <td>
            <MeetingName kind={meeting.kind} /> {meeting.kind === GqlMeetingKind.BoardMeeting && ` ${meeting.number}`}
          </td>
          <td>
            <DateFormat locale={i18n.locale}>{meeting.startTime}</DateFormat>
          </td>
          <td>
            <MeetingProgress
              summonStatus={meeting.summonStatus}
              meetingDate={DateTime.fromISO(meeting.startTime)}
              scriveSignatureStatus={meeting.latestMinutesDocument?.scriveStatus}
              zignedAgreementStatus={meeting.latestMinutesDocument?.zignedAgreementStatus}
              meetingMinutesReady={meeting.agendaItemsDone}
            />
          </td>
        </Tr>
      ))}
      emptyState={
        <EmptyState
          header={<Trans id="You haven't created any Meetings yet. Get started by creating your first one." />}
          cta={
            <Button size="sm" component={Link} to={link('/board-portal/board-meetings')}>
              <Trans id="Create meeting" />
            </Button>
          }
        />
      }
    />
  );
};
